import { useState, useMemo } from 'react';
import { breakpoints } from '@naf/theme';
import { useMediaQuery } from './useMediaQuery';
import { BenefitPageType, BenefitItemRecordType } from '../../../types/BenefitsOverviewType';

export const useActiveCampaigns = (pages: BenefitItemRecordType) => {
  const [showAll, setShowAll] = useState(false);
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.s})`);

  const filteredPages = useMemo(
    () =>
      Object.entries(pages).reduce((acc, [key, page]) => {
        const filteredData =
          page.data?.filter(
            (item) => item.campaignDetails?.endDate && new Date(item.campaignDetails.endDate) > new Date(),
          ) || [];

        acc[key] = { ...page, data: filteredData };
        return acc;
      }, {} as BenefitItemRecordType),
    [pages],
  );

  const hasResults = useMemo(
    () => Object.values(filteredPages).some((page) => page.data && page.data.length > 0),
    [filteredPages],
  );

  const allBenefitPages = useMemo(() => {
    const benefits = [];
    Object.entries(filteredPages).forEach(([key, page]) => {
      if (page.data && page.data.length > 0) {
        page.data.forEach((benefit) => {
          benefits.push({
            key,
            benefit,
          });
        });
      }
    });
    return benefits as BenefitPageType[];
  }, [filteredPages]);

  const limitedBenefitPages = useMemo(
    () => (showAll ? allBenefitPages : [...allBenefitPages].slice(0, 3)),
    [showAll, allBenefitPages],
  );

  const displayedBenefits = useMemo(() => {
    const result = {};

    limitedBenefitPages.forEach(({ key, benefit }) => {
      if (!result[key]) {
        result[key] = {
          data: [],
          meta: filteredPages[key].meta,
        };
      }
      result[key].data.push(benefit);
    });

    return result as BenefitItemRecordType;
  }, [limitedBenefitPages, filteredPages]);

  const benefitItems = useMemo(() => {
    if (!isMobile) return [];
    return allBenefitPages.map((page, index) => ({
      id: `${page.benefit.id}-${index}`,
      benefit: page.benefit,
      itemKey: `item-${page.benefit.id}-${index}`,
    }));
  }, [allBenefitPages, isMobile]);

  const toggleShowAll = () => setShowAll((prev) => !prev);

  return {
    hasResults,
    showAll,
    allBenefitPages,
    limitedBenefitPages,
    displayedBenefits,
    benefitItems,
    toggleShowAll,
    isMobile,
  };
};
